import React from "react";
import { PRODUCTS } from "../data";
import { SectionLabel } from "../components/ui/Typography";
import { BtnPrimary, BtnOrange } from "../components/ui/Buttons";
import CTABanner from "../components/ui/CTABanner";
import useScrollFade from "../hooks/useScrollFade";

/* ── Stats card used in SmartVision & ReplenishAI ── */
const StatCard = ({ val, label, chg, up }) => (
  <div className="bg-white/8 border border-white/10 rounded-xl p-4">
    <div className="font-head font-bold text-2xl text-white">{val}</div>
    <div className="text-white/40 text-xs mt-1">{label}</div>
    <div className={`text-xs font-semibold mt-1 ${up ? "text-emerald-400" : "text-red-400"}`}>{chg}</div>
  </div>
);

/* ── Feature list ── */
const FeatureList = ({ features, color = "text-blue-600" }) => (
  <ul className="space-y-3 mb-8">
    {features.map(f => (
      <li key={f} className="flex items-start gap-3 text-slate-600 text-sm">
        <span className={`font-bold mt-0.5 shrink-0 ${color}`}>✓</span>
        {f}
      </li>
    ))}
  </ul>
);

/* ── SmartVision product block ── */
const SmartVisionBlock = ({ product, navigate }) => {
  const ref = useScrollFade();
  return (
    <div ref={ref} className="fade-in-section grid lg:grid-cols-2 gap-16 items-center">
      <div>
        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${product.iconBg} flex items-center justify-center text-3xl mb-5`}>{product.emoji}</div>
        <span className={`inline-block text-xs font-head font-bold px-3 py-1 rounded-full border mb-4 ${product.tagClass}`}>Video Analytics Platform</span>
        <h2 className="font-head font-extrabold text-4xl text-slate-900 mb-4">{product.name}</h2>
        <p className="text-slate-500 text-base leading-relaxed mb-6">{product.longDesc}</p>
        <FeatureList features={product.features} color="text-blue-600" />
        <BtnPrimary onClick={() => navigate("demo")}>
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
          </svg>
          Watch Demo
        </BtnPrimary>
      </div>
      <div className="bg-gradient-to-br from-slate-900 to-blue-950 rounded-2xl p-7 text-white">
        <div className="text-white/45 text-xs font-head font-semibold uppercase tracking-widest mb-4">Live Analytics View</div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          {product.stats.map(s => <StatCard key={s.label} {...s} />)}
        </div>
        <div className="bg-white/5 border border-white/8 rounded-xl p-3 text-xs text-white/45 font-head">
          🟢 {product.live}
        </div>
      </div>
    </div>
  );
};

/* ── ReplenishAI block ── */
const ReplenishBlock = ({ product, navigate }) => {
  const ref = useScrollFade();
  return (
    <div ref={ref} className="fade-in-section grid lg:grid-cols-2 gap-16 items-center">
      <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 border border-orange-100">
        <div className="grid grid-cols-3 gap-3 mb-6">
          {product.stats.slice(0,3).map(s => (
            <div key={s.label} className="bg-white rounded-xl p-4 text-center border border-slate-200 shadow-sm">
              <div className="font-head font-bold text-xl text-slate-900">{s.val}</div>
              <div className="text-slate-400 text-xs mt-1">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm">
          <div className="font-head font-semibold text-slate-700 text-sm mb-3">Replenishment Queue (Live)</div>
          <div className="space-y-2.5">
            {product.queue.map(q => (
              <div key={q.name} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{q.name}</span>
                <span className={`px-2.5 py-0.5 rounded-md text-xs font-semibold font-head ${q.cls}`}>{q.badge}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div>
        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${product.iconBg} flex items-center justify-center text-3xl mb-5`}>{product.emoji}</div>
        <span className={`inline-block text-xs font-head font-bold px-3 py-1 rounded-full border mb-4 ${product.tagClass}`}>Inventory Intelligence</span>
        <h2 className="font-head font-extrabold text-4xl text-slate-900 mb-4">{product.name}</h2>
        <p className="text-slate-500 text-base leading-relaxed mb-6">{product.longDesc}</p>
        <FeatureList features={product.features} color="text-orange-500" />
        <BtnOrange onClick={() => navigate("demo")}>
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
          </svg>
          Watch Demo
        </BtnOrange>
      </div>
    </div>
  );
};

/* ── RetailIQ & AuditApp side-by-side cards ── */
const SmallProductCard = ({ product, navigate }) => {
  const ref = useScrollFade();
  return (
    <div ref={ref} className="fade-in-section card-hover bg-white border border-slate-200 rounded-2xl p-8">
      <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${product.iconBg} flex items-center justify-center text-2xl mb-5`}>{product.emoji}</div>
      <span className={`inline-block text-xs font-head font-bold px-3 py-1 rounded-full border mb-4 ${product.tagClass}`}>{product.tag}</span>
      <h3 className="font-head font-extrabold text-2xl text-slate-900 mb-3">{product.name}</h3>
      <p className="text-slate-500 text-sm leading-relaxed mb-5">{product.longDesc}</p>
      <FeatureList features={product.features} color="text-blue-600" />
      <button
        onClick={() => navigate("demo")}
        className="inline-flex items-center gap-2 text-blue-600 font-head font-semibold text-sm hover:gap-4 transition-all"
      >
        Watch Demo
        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"/>
        </svg>
      </button>
    </div>
  );
};

/* ── Page ── */
const ProductsPage = ({ navigate }) => (
  <div className="page-enter">
    {/* Hero */}
    <section className="bg-gradient-to-br from-slate-950 via-[#0d2151] to-[#0e2660] relative overflow-hidden pt-32 pb-20 text-center">
      <div className="hero-grid-bg absolute inset-0 opacity-50 pointer-events-none" />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6">
        <SectionLabel light>Our Products</SectionLabel>
        <h1 className="font-head font-black text-5xl md:text-6xl text-white mt-3 mb-5 tracking-tight leading-tight">
          Flagship <span className="gradient-text">AI Products</span>
        </h1>
        <p className="text-white/60 text-xl">
          Purpose-built, production-ready AI software that delivers measurable business outcomes.
        </p>
      </div>
    </section>

    {/* Product blocks */}
    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-24">
        <SmartVisionBlock product={PRODUCTS[0]} navigate={navigate} />
        <ReplenishBlock   product={PRODUCTS[1]} navigate={navigate} />
        <div className="grid md:grid-cols-2 gap-8">
          <SmallProductCard product={PRODUCTS[2]} navigate={navigate} />
          <SmallProductCard product={PRODUCTS[3]} navigate={navigate} />
        </div>
      </div>
    </section>

    <CTABanner
      navigate={navigate}
      title="See These Products in Action"
      sub="Request a live demo tailored to your industry and business needs."
      btn1="Watch Demo"
      btn2="Request Pricing"
      page1="demo"
      page2="contact"
    />
  </div>
);

export default ProductsPage;
